import { useState, useCallback } from 'react';
import { supabaseData } from '../../utils/supabase-data-client';
import { refetchAndCache } from './refetchAndCache';
import { useCacheStore } from './useCacheStore';
import { MutationResult } from './useCategoryMutations';
import { Note } from '../../types/notes';

export type NewNoteInput = {
  note_title: string;
  note_content?: string;
  category_id: string | null;
};

export type NoteUpdateInput = {
  note_title?: string;
  note_content?: string;
};

export type AddNoteResult = MutationResult & { note?: Note };

interface UseNoteMutationsReturn {
  addNote: (input: NewNoteInput) => Promise<AddNoteResult>;
  updateNote: (
    noteId: string,
    updates: NoteUpdateInput,
  ) => Promise<MutationResult>;
  moveNote: (
    noteId: string,
    newCategoryId: string | null,
  ) => Promise<MutationResult>;
  softDeleteNote: (noteId: string) => Promise<MutationResult>;
  restoreNote: (
    noteId: string,
    fallbackCategoryId?: string | null,
  ) => Promise<MutationResult>;
  hardDeleteNote: (noteId: string) => Promise<MutationResult>;
  loading: boolean;
}

function validateTitle(title: string): { valid: boolean; trimmed: string } {
  const trimmed = title.trim();
  if (trimmed.length < 1 || trimmed.length > 255) {
    return { valid: false, trimmed };
  }
  return { valid: true, trimmed };
}

/**
 * CRUD for notes. Delete is soft (sets deleted_at); restore clears it (with a
 * fallback category when the original was removed); hardDelete is a real delete
 * used only from the Trash view.
 */
export const useNoteMutations = (): UseNoteMutationsReturn => {
  const [loading, setLoading] = useState(false);
  const cacheStore = useCacheStore();

  const addNote = useCallback(
    async (input: NewNoteInput): Promise<AddNoteResult> => {
      const { valid, trimmed } = validateTitle(input.note_title);
      if (!valid) {
        return {
          success: false,
          error: 'Note title must be between 1 and 255 characters.',
        };
      }

      setLoading(true);
      try {
        const { data, error } = await supabaseData
          .from('notes')
          .insert({
            note_title: trimmed,
            note_content: input.note_content ?? '',
            category_id: input.category_id,
          })
          .select('*')
          .single();

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('notes', cacheStore);
        return { success: true, note: data as Note };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore],
  );

  const updateNote = useCallback(
    async (
      noteId: string,
      updates: NoteUpdateInput,
    ): Promise<MutationResult> => {
      const patch: Record<string, unknown> = {
        last_modified: new Date().toISOString(),
      };

      if (updates.note_title !== undefined) {
        const { valid, trimmed } = validateTitle(updates.note_title);
        if (!valid) {
          return {
            success: false,
            error: 'Note title must be between 1 and 255 characters.',
          };
        }
        patch.note_title = trimmed;
      }
      if (updates.note_content !== undefined) {
        patch.note_content = updates.note_content;
      }

      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('notes')
          .update(patch)
          .eq('note_id', noteId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('notes', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore],
  );

  const moveNote = useCallback(
    async (
      noteId: string,
      newCategoryId: string | null,
    ): Promise<MutationResult> => {
      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('notes')
          .update({ category_id: newCategoryId })
          .eq('note_id', noteId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('notes', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore],
  );

  const softDeleteNote = useCallback(
    async (noteId: string): Promise<MutationResult> => {
      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('notes')
          .update({ deleted_at: new Date().toISOString() })
          .eq('note_id', noteId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('notes', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore],
  );

  const restoreNote = useCallback(
    async (
      noteId: string,
      fallbackCategoryId: string | null = null,
    ): Promise<MutationResult> => {
      setLoading(true);
      try {
        // Determine whether the note's original category still exists; if not,
        // reassign to the provided fallback (typically Uncategorized).
        const { data: existing, error: readError } = await supabaseData
          .from('notes')
          .select('category_id')
          .eq('note_id', noteId)
          .single();

        if (readError) {
          return { success: false, error: readError.message };
        }

        const patch: Record<string, unknown> = { deleted_at: null };
        const originalCategoryId = (existing as { category_id: string | null })
          .category_id;

        if (originalCategoryId) {
          const categories = cacheStore.getNoteCategories();
          const stillExists = categories
            ? categories.some((c) => c.category_id === originalCategoryId)
            : true;
          if (!stillExists) {
            patch.category_id = fallbackCategoryId;
          }
        }

        const { error } = await supabaseData
          .from('notes')
          .update(patch)
          .eq('note_id', noteId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('notes', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore],
  );

  const hardDeleteNote = useCallback(
    async (noteId: string): Promise<MutationResult> => {
      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('notes')
          .delete()
          .eq('note_id', noteId);

        if (error) {
          return { success: false, error: error.message };
        }

        // Not in the active cache (it was trashed) — no active refetch needed.
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    addNote,
    updateNote,
    moveNote,
    softDeleteNote,
    restoreNote,
    hardDeleteNote,
    loading,
  };
};
